// RollupEditorForm.jsx

import React from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// CSS
import './RollupEditorForm.css';
import './RollupModal.css';

// Components
import Button from 'components/button/Button';
import Checkbox from 'components/checkbox/Checkbox';
import ColorButton from 'components/colorbutton/ColorButton';
import ModalField from 'components/modal/field/ModalField';
import ModalForm from 'components/modal/form/ModalForm';
import ModalHint from 'components/modal/hint/ModalHint';
import ModalLabel from 'components/modal/label/ModalLabel';
import TextInput from 'components/textinput/TextInput';
import {
  normalizeRollupValueColor,
  ROLLUP_VALUE_COLOR_AUTO,
} from 'rollups/rollupDefinitions';

/**
 * RollupEditorForm
 *
 * Shared rollup definition form for modal and Studio embedded editors.
 *
 * @returns {ReactNode} The rendered component.
 */
const RollupEditorForm = React.memo(({
  aggregateOptions = [],
  draft,
  error = '',
  fieldOptions = [],
  onAggregateChange,
  onDraftChange,
  onFieldChange,
  onTitleChange,
  usesCustomColor = false,
  width = '100%',
}) => {

  // ----- CONTEXT ----- //

  const { theme } = useTheme();

  // ----- RENDER ----- //

  return (
    <ModalForm width={width}>
      <ModalField>
        <ModalLabel label="Title" />
        <ModalHint hint="The title of the rollup." />

        <TextInput
          autoFocus
          error={!!error}
          helperText={error}
          maxLength={100}
          placeholder='e.g. "Average Revenue"'
          value={draft?.title || ''}
          onChange={(event) => onTitleChange?.(event.target.value)}
        />
      </ModalField>

      <ModalField>
        <ModalLabel label="Description" />
        <ModalHint hint="Optional context for this summary card." />

        <TextInput
          expandable
          maxLength={240}
          multiline
          placeholder="Optional context for this summary card"
          showCount
          value={draft?.description || ''}
          onChange={(event) => onDraftChange?.({ description: event.target.value })}
        />
      </ModalField>

      <div className="rollup-modal-config-row">

        {/* FIELD */}
        <div className="rollup-modal-field">
          <ModalLabel label="Field" />

          <select
            className="rollup-modal-select"
            style={{
              backgroundColor: theme.panel,
              borderColor: theme.backgroundHover,
              color: theme.foreground,
            }}
            value={draft?.fieldKey || ''}
            onChange={(event) => onFieldChange?.(event.target.value)}
          >
            {fieldOptions.map((option) => (
              <option key={option.key} value={option.key}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {/* FIELD */}
        <div className="rollup-modal-field">
          <ModalLabel label="Aggregate" />

          <select
            className="rollup-modal-select"
            style={{
              backgroundColor: theme.panel,
              borderColor: theme.backgroundHover,
              color: theme.foreground,
            }}
            value={draft?.aggregate || ''}
            onChange={(event) => onAggregateChange?.(event.target.value)}
          >
            {aggregateOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {/* COLOR */}
        <div className="rollup-modal-color-field">

          {/* COLOR ROW */}
          <div className="rollup-modal-color-row">
            <ColorButton
              color={usesCustomColor ? draft?.valueColor : ''}
              onChange={(nextColor) => onDraftChange?.({
                valueColor: normalizeRollupValueColor(nextColor),
              })}
            />
            {usesCustomColor ? (
              <Button
                borderRadius="9px"
                height="38px"
                label="Auto"
                onClick={() => onDraftChange?.({ valueColor: ROLLUP_VALUE_COLOR_AUTO })}
              />
            ) : null}
          </div>
        </div>
      </div>

      {/* TOGGLES */}
      <div className="rollup-modal-toggles">

        {/* TOGGLE */}
        <div
          className="rollup-modal-toggle"
        >
          <Checkbox
            label="Pin to Database Summary"
            value={draft?.pinToDatabaseSummary === true}
            onChange={() => onDraftChange?.({
              pinToDatabaseSummary: !(draft?.pinToDatabaseSummary === true),
            })}
          />
        </div>

        {/* TOGGLE */}
        <div
          className="rollup-modal-toggle"
        >
          <Checkbox
            label="Pin to Related Summary"
            value={draft?.pinToRelatedSummary === true}
            onChange={() => onDraftChange?.({
              pinToRelatedSummary: !(draft?.pinToRelatedSummary === true),
            })}
          />
        </div>
      </div>

      {/* ERROR */}
      {error ? (
        <div
          className="rollup-modal-error"
          style={{
            color: theme.danger || '#d64545',
          }}
        >
          {error}
        </div>
      ) : null}
    </ModalForm>
  );
});

// Dev Tools
RollupEditorForm.displayName = 'RollupEditorForm';

export default RollupEditorForm;
